import React, { useState, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa6";
import { useGameStage } from "../hooks/useGameStage";
import { GAME_STAGES, DEFAULT_ROOM, HOME_PATH } from "../constants/gameStages";
import { SocketContext } from "../context/SocketProvider";
import { useLanguage } from "../context/LanguageContext";

/**
 * A button component that opens an overlay to confirm returning home.
 * Features keyboard navigation: ESC to cancel, ENTER to go home.
 * Can render as either a text button or a logo image.
 */
const ReturnHomeButton = ({
  className = "w-full px-4 py-2 text-left hover:bg-gray-100 rounded text-black",
  variant = "button", // "button" or "logo"
  logoSrc = null,
  logoAlt = "Logo",
  logoClassName = "",
  onConfirm = null, // Optional callback when home navigation is confirmed
}) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const navigate = useNavigate();
  const { updateStage } = useGameStage();
  const { socket, role, gameStage } = useContext(SocketContext);
  const { t } = useLanguage();

  const color =
    gameStage && gameStage === GAME_STAGES.RESULTS
      ? "yellow"
      : role === "describer"
      ? "pink"
      : "blue";

  const handleCancel = () => {
    setShowOverlay(false);
  };

  const handleGoHome = () => {
    // Call the optional callback first (e.g., to close modals)
    if (onConfirm) {
      onConfirm();
    }

    // Notify the other player that someone is leaving the game
    socket.emit("player_left_game", {
      room: DEFAULT_ROOM,
    });

    // Reset the room immediately since player is leaving (not voting for new game)
    socket.emit("reset_room", {
      room: DEFAULT_ROOM,
      resetMode: true,
      immediate: true, // This parameter tells backend to skip voting
    });

    updateStage(GAME_STAGES.READY);
    setShowOverlay(false);
    navigate(HOME_PATH);
  };

  // Handle keyboard navigation when overlay is open
  useEffect(() => {
    if (!showOverlay) return;

    const handleKeyPress = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleCancel();
      } else if (event.key === "Enter") {
        event.preventDefault();
        handleGoHome();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    // Focus management - focus the first button when overlay opens
    const focusableElements = document.querySelectorAll(
      ".return-home-overlay button"
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [showOverlay]);

  return (
    <>
      {/* Render based on variant */}
      {variant === "logo" ? (
        <img
          src={logoSrc}
          alt={logoAlt}
          className={`cursor-pointer hover:opacity-80 transition-opacity ${logoClassName}`}
          onClick={() => setShowOverlay(true)}
        />
      ) : (
        <button className={className} onClick={() => setShowOverlay(true)}>
          {t('settings.returnHome', 'Return to Home')}
        </button>
      )}

      {showOverlay &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] w-full h-full bg-black/30 overflow-hidden flex items-center justify-center"
            role="dialog"
            aria-modal="true"
          >
            <article className="return-home-overlay w-[514px] h-48 rounded-2xl overflow-hidden relative bg-white">
              <header
                className={`w-[514px] h-16 bg-${color}400 flex items-center justify-center`}
              >
                <h2 className="text-center text-white text-xl font-bold font-['Gabarito'] px-4">
                  {t('modal.leaveGame', 'Are you sure you want to leave the game?')}
                </h2>
              </header>
              <main className="w-[514px] h-32 bg-white relative flex items-end justify-center gap-3 pb-4">
                <button
                  className={`btn-${color}-sm-transparent`}
                  onClick={handleCancel}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  className={`btn-${color}-sm relative`}
                  onClick={handleGoHome}
                >
                  <span className="btn-text-pos-right-sm">{t('common.goHome', 'Go Home')}</span>
                  <FaChevronRight className="arrow-pos-right-sm" />
                </button>
              </main>
            </article>
          </div>,
          document.body
        )}
    </>
  );
};

export default ReturnHomeButton;
